import { ETransactionStatus, ETransactionType } from '@xeppt/xeppt-sdk/types';
import Typography from '@components/common/typography';
import { Icon } from '@components/icons';
import Status from '@components/common/status';
import { prettifyAmount, formatDate, getTransactionStatus } from '@utils/index';
import styles from './purchase_detail_drawer.module.scss';

// ─── Purchase (Transaction) Detail Drawer ─────────────────────────────────────
// Right-side drawer for viewing a single order-linked transaction. Shared
// across every dev-portal surface that lists these transactions — Orders
// (Transactions tab), Sales, and Payments — so merchants see one consistent
// transaction-detail experience instead of the customer-facing view_transaction
// modal. Renders merchant-facing copy (positive amount, "Amount you received")
// for the common credit/purchase case, matching Figma node 19489:127491, and
// degrades gracefully to refund-style copy if a debit entry ever surfaces in
// one of these lists.
//
// `order` intentionally accepts a minimal info bag rather than a full fetched
// order — every field is optional so callers that only have a transaction's
// own order_id/description/source fields (Sales, Payments) can build a plain
// object inline instead of firing a second network request. `TOrderWithId`
// (Orders' own richer type) already satisfies this shape structurally.

export interface PurchaseDetailOrderInfo {
    customer_display_name?: string;
    customer_id?: string;
    currency?: string;
    order_id?: string;
    description?: string;
}

export interface PurchaseDetailTxn {
    id: string;
    type: ETransactionType;
    amount: number;
    currency?: string;
    status: ETransactionStatus;
    // Callers pass a TTransaction whose created_at is a Date; formatDate accepts both.
    created_at: string | Date;
    points?: number;
}

export interface PurchaseDetailDrawerProps {
    txn: PurchaseDetailTxn;
    order: PurchaseDetailOrderInfo;
    onClose: () => void;
    onViewOrder: () => void;
}

function nameInitials(displayName: string): string {
    const parts = displayName.trim().split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return displayName.slice(0, 2).toUpperCase();
}

function handleInitials(handle: string): string {
    const clean = handle.replace(/^@/, '');
    if (!clean) return '??';
    const parts = clean.split(/[._-]/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return clean.slice(0, 2).toUpperCase();
}

function txnStatusLabel(status: ETransactionStatus): string {
    switch (status) {
        case ETransactionStatus.PENDING: return 'Pending';
        case ETransactionStatus.APPROVED: return 'Approved';
        case ETransactionStatus.DECLINED: return 'Declined';
        case ETransactionStatus.CANCELED: return 'Canceled';
        case ETransactionStatus.REFUND: return 'Refund';
        case ETransactionStatus.ERROR: return 'Error';
        case ETransactionStatus.RETURNED: return 'Returned';
        case ETransactionStatus.EXPIRED: return 'Expired';
        default: return 'Unknown';
    }
}

const PurchaseDetailDrawer = ({ txn, order, onClose, onViewOrder }: PurchaseDetailDrawerProps) => {
    const currency = txn.currency ?? order.currency ?? 'CAD';
    const initials = order.customer_display_name
        ? nameInitials(order.customer_display_name)
        : handleInitials(order.customer_id ?? '');
    // DEBIT = merchant leg: money enters the merchant's wallet = a received purchase,
    // shown positive (see the EFundsOperation convention in src/types/enum.ts, and
    // OrderDetailModal which treats DEBIT as `+`). Non-DEBIT (CREDIT) = outgoing/refund
    // leg, shown negative.
    const isReceived = txn.type === ETransactionType.DEBIT;
    const amountLabel = isReceived ? 'Amount you received' : 'Amount charged';
    const amountValue = `${isReceived ? '' : '-'}$${prettifyAmount(Number(txn.amount).toFixed(2))} ${currency}`;
    return (
        <div className={styles.panel_overlay} onClick={onClose}>
            <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
                <div className={styles.panel_header}>
                    <Typography variant="body2" weight="semibold" className={styles.panel_title}>
                        Transaction details
                    </Typography>
                    <button
                        type="button"
                        className={styles.panel_close}
                        onClick={onClose}
                        aria-label="Close">
                        <Icon name="close" width={18} height={18} />
                    </button>
                </div>
                <div className={styles.panel_body}>
                    <div className={styles.rd_body_inner}>
                        <div className={styles.pd_user_card}>
                            <div className={styles.pd_recipient_info}>
                                <div className={styles.pd_avatar_wrapper}>
                                    <div className={styles.pd_avatar}>{initials}</div>
                                    <button
                                        type="button"
                                        className={styles.pd_avatar_kebab}
                                        aria-label="More options">
                                        ⋮
                                    </button>
                                </div>
                                {order.customer_display_name && (
                                    <Typography variant="body2" weight="semibold" className={styles.panel_name}>
                                        {order.customer_display_name}
                                    </Typography>
                                )}
                                <Typography variant="body4" className={styles.panel_handle}>
                                    @{order.customer_id}
                                </Typography>
                                <Typography variant="body4" className={styles.panel_xeppt_label}>
                                    XEPPT user
                                </Typography>
                            </div>
                            <div className={styles.pd_pill_row}>
                                <span className={styles.pd_pill_line} />
                                <span className={styles.pd_pill}>
                                    {isReceived ? '↓ Purchase' : '↑ Refund'}
                                </span>
                                <span className={styles.pd_pill_line} />
                            </div>
                            <Typography variant="h4" weight="semibold" className={styles.panel_amount}>
                                {amountValue}
                            </Typography>
                            <Status variant={getTransactionStatus(txn.status)}>
                                {txnStatusLabel(txn.status)}
                            </Status>
                        </div>
                        {order.order_id && (
                            <div className={styles.pd_info_banner}>
                                <span className={styles.pd_info_banner_icon}>
                                    <Icon name="info" width={24} height={24} />
                                </span>
                                <p className={styles.pd_info_banner_text}>
                                    {`This transaction was made for order #${order.order_id}.`}
                                    {'  '}
                                    <button
                                        type="button"
                                        className={styles.pd_info_banner_link}
                                        onClick={onViewOrder}>
                                        View order
                                    </button>
                                </p>
                            </div>
                        )}
                        <div className={styles.rd_details_section}>
                            <Typography variant="body2" weight="semibold" className={styles.rd_details_header}>
                                Details
                            </Typography>
                            <div className={[styles.detail_rows, styles.pd_rows].join(' ')}>
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Date and time</span>
                                    <span className={styles.detail_value}>
                                        {formatDate(txn.created_at, 'DD MMMM YYYY, HH:mm')}
                                    </span>
                                </div>
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>{amountLabel}</span>
                                    <span className={[styles.detail_value, styles.pd_value_bold].join(' ')}>
                                        {amountValue}
                                    </span>
                                </div>
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Points you earned</span>
                                    <span className={styles.detail_value}>{txn.points ?? 0}</span>
                                </div>
                                {order.order_id && (
                                    <div className={styles.detail_row}>
                                        <span className={styles.detail_label}>Order ID</span>
                                        <span className={styles.detail_value}>#{order.order_id}</span>
                                    </div>
                                )}
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Description</span>
                                    <span className={styles.detail_value}>{order.description || '—'}</span>
                                </div>
                                <div className={styles.detail_row}>
                                    <span className={styles.detail_label}>Transaction ID</span>
                                    <span className={styles.detail_value}>{txn.id}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={styles.panel_footer}>
                    <button
                        type="button"
                        className={styles.detail_report_btn}
                        // TODO: wire support ticket flow
                        onClick={() => { /* no-op until support integration */ }}>
                        Report problem
                    </button>
                </div>
            </div>
        </div>
    );
};

export default PurchaseDetailDrawer;
